import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  Building,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Quote,
} from "lucide-react";
import Grid from "../Grid/grid";
import Title from "../title";
import axios from "axios";
import Loader from "../loader/loader";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "react-device-frameset/styles/marvel-devices.min.css";

function Testimonial() {
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const fetchTestimonials = () => {
    axios
      .get(`testimonials`)
      .then((res) => {
        setLoading(false);
        setTestimonials(res.data);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || testimonials.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  return (
    <section className="w-full h-auto relative flex flex-col lg:p-16 p-8 overflow-hidden">
      <Grid size={100} direction="top-left" />
      <div className="z-10 flex justify-start items-start mb-12">
        <Title title="What our customers say" />
      </div>
      {loading ? (
        <Loader />
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="z-10 flex gap-3 items-center justify-center mb-8"
          >
            <button
              onClick={prevTestimonial}
              className="h-12 w-12 flex items-center justify-center border rounded-full bg-white/80 backdrop-blur-sm border-slate-200 hover:bg-white hover:scale-110 transition-all duration-300 shadow-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              onClick={toggleAutoPlay}
              className="h-12 w-12 flex items-center justify-center border rounded-full bg-white/80 backdrop-blur-sm border-slate-200 hover:bg-white hover:scale-110 transition-all duration-300 shadow-lg"
            >
              {isAutoPlaying ? (
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY }}
                >
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                </motion.div>
              ) : (
                <div className="w-0 h-0 border-l-[6px] border-l-slate-600 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5" />
              )}
            </button>

            <button
              onClick={nextTestimonial}
              className="h-12 w-12 flex items-center justify-center border rounded-full bg-white/80 backdrop-blur-sm border-slate-200 hover:bg-white hover:scale-110 transition-all duration-300 shadow-lg"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </motion.div>
          <div className="z-10 flex-1 flex items-center justify-center px-2 sm:px-4">
            <div className="w-full max-w-6xl relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 100, rotateY: -15 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0 }}
                  exit={{ opacity: 0, x: -100, rotateY: 15 }}
                  transition={{
                    duration: 0.6,
                    type: "spring",
                    stiffness: 100,
                    damping: 20,
                  }}
                  className="w-full"
                >
                  <div className="w-full bg-gradient-to-br from-white via-white to-slate-50/50 backdrop-blur-sm border border-slate-200/50 shadow-xl sm:shadow-2xl hover:shadow-3xl transition-all duration-500 overflow-hidden group rounded-2xl">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="relative p-4 sm:p-6 lg:p-8 xl:p-12 flex flex-col min-h-[300px] sm:min-h-[350px] lg:min-h-[400px]">
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.3, type: "spring" }}
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 lg:top-8 lg:right-8 p-2 sm:p-3 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full"
                      >
                        <Quote className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-slate-400" />
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="flex-1 mb-6 sm:mb-8 pr-8 sm:pr-12"
                      >
                        <p className="text-slate-700 text-base sm:text-lg lg:text-xl xl:text-2xl leading-relaxed font-medium">
                          "{testimonials[currentIndex]?.message}"
                        </p>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="border-t border-slate-200 pt-4 sm:pt-6"
                      >
                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-800 mb-2">
                          {testimonials[currentIndex]?.person_name}
                        </h3>
                        <div className="flex items-start sm:items-center gap-2 text-slate-600">
                          <Building className="w-4 h-4 flex-shrink-0 mt-0.5 sm:mt-0" />
                          <span className="text-xs sm:text-sm lg:text-base leading-tight sm:leading-normal">
                            {testimonials[currentIndex]?.position} at{" "}
                            {testimonials[currentIndex]?.company_name}
                          </span>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="z-10 flex justify-center gap-1.5 sm:gap-2 mt-6 sm:mt-8"
          >
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "bg-slate-600 scale-125"
                    : "bg-slate-300 hover:bg-slate-400"
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </motion.div>
        </>
      )}
    </section>
  );
}

export default Testimonial;
